import type { ResponseStatus } from '@/domains/survey/api/contracts';
import type { DemoRole } from './roles';
import type {
  Eligibility,
  QualityRating,
  RankingFamily,
  SurveyType,
  Travel
} from './vocabulary';

/**
 * The demo's cast: about twelve advisors, three teams (each a team survey's record of its own), one
 * RIA firm and one FSP, and the Veritant staff who work them — the same people in every act. Dana Whitfield is seen submitting in act 1, flagged
 * in act 3 and placed in act 4, so a viewer following her follows the whole cycle.
 *
 * **Everybody here is invented.** No name, CRD, address or advisor comes from Salesforce, UAT or
 * the legacy data: those are production personal data. CRDs are in the Development seed's
 * `9xxxxxx` style, addresses are at `example.com` (RFC 2606's), and the RIA firm's SEC number is
 * above any the SEC has issued.
 *
 * **Firms are real only where a product rule names them** — Merrill, Morgan Stanley, UBS, Wells
 * Fargo, Raymond James and LPL, the wire firms the team gating branches on (TEAM-AC-004). Nothing
 * said about them here is a fact about them.
 *
 * Where a fixture shows something survey-api already answers, it takes survey-api's shape: a
 * response's status is `ResponseStatus`, and a number's type is the `idType` survey-api spells.
 *
 * Amounts are US dollars in millions. Dates are ISO days, and the demo's today is `DEMO_TODAY`, so
 * that "due in three days" still reads the same next month.
 */

export const DEMO_TODAY = '2026-10-01';
export const DEMO_CYCLE = '2027';

/* ------------------------------------------------------------------------------------------------
 * Veritant's staff
 * ---------------------------------------------------------------------------------------------- */

export interface IDemoStaff {
  id: string;
  name: string;
  role: Exclude<DemoRole, 'advisor'>;
  email: string;
}

export const DEMO_STAFF: readonly IDemoStaff[] = [
  { id: 's-priya', name: 'Priya Natarajan', role: 'analyst', email: 'priya.natarajan@example.com' },
  { id: 's-leo', name: 'Leo Fairbanks', role: 'analyst', email: 'leo.fairbanks@example.com' },
  { id: 's-grace', name: 'Grace Holloway', role: 'compliance', email: 'grace.holloway@example.com' },
  { id: 's-elena', name: 'Elena Duarte', role: 'data_team', email: 'elena.duarte@example.com' },
  { id: 's-tomas', name: 'Tomás Rivera', role: 'survey_owner', email: 'tomas.rivera@example.com' },
  { id: 's-ada', name: 'Ada Kim', role: 'system_admin', email: 'ada.kim@example.com' }
];

export function staffName(id: Nullable<string>): string {
  if (!id) return 'Unassigned';
  return DEMO_STAFF.find((member) => member.id === id)?.name ?? id;
}

/** Who "me" is on a screen of one's own — the DD list — for the role being viewed as:
 *  the cast's member of that role, and the first analyst when viewing as signed in. */
export function staffFor(role: Nullable<DemoRole>): Nullable<IDemoStaff> {
  if (role === 'advisor') return null;
  return DEMO_STAFF.find((member) => member.role === (role ?? 'analyst')) ?? null;
}

/* ------------------------------------------------------------------------------------------------
 * Firms
 * ---------------------------------------------------------------------------------------------- */

/** A firm the cast works at, by name. */
export interface IDemoFirm {
  id: string;
  name: string;
}

export const DEMO_FIRMS: readonly IDemoFirm[] = [
  { id: 'f-merrill', name: 'Merrill Lynch' },
  { id: 'f-ms', name: 'Morgan Stanley Wealth Management' },
  { id: 'f-ubs', name: 'UBS Financial Services' },
  { id: 'f-wells', name: 'Wells Fargo Advisors' },
  { id: 'f-rj', name: 'Raymond James' },
  { id: 'f-lpl', name: 'LPL Financial' },
  { id: 'f-harborline', name: 'Harborline Wealth Partners LLC' },
  { id: 'f-northstar', name: 'Northstar Mutual Agency' }
];

export function firmName(id: string): string {
  return DEMO_FIRMS.find((firm) => firm.id === id)?.name ?? id;
}

/* ------------------------------------------------------------------------------------------------
 * People
 * ---------------------------------------------------------------------------------------------- */

export interface IDemoDisclosure {
  id: string;
  type: 'Customer dispute' | 'Regulatory' | 'Employment separation' | 'Financial' | 'Criminal';
  eventDate: string;
  status: 'Pending' | 'Settled' | 'Denied' | 'Closed — no action' | 'Final';
  allegation: string;
}

export interface IFieldChange {
  date: string;
  value: string;
  by: string;
}

export interface IDemoPerson {
  id: string;
  name: string;
  /** survey-api's `idType` for the anchoring number, and its value. */
  idType: 'CRD' | 'NPN' | 'SEC_NUMBER';
  number: string;
  email: string;
  firmId: string;
  city: string;
  state: string;
  survey: SurveyType;
  aum: number;
  production: number;
  status2027: ResponseStatus;
  /** REC-06's survey submitted and the advisor's last edit. */
  submittedAt: Nullable<string>;
  lastAdvisorEdit: string;
  // The internal fields.
  quality: Nullable<QualityRating>;
  travel: Nullable<Travel>;
  eligibility: Eligibility;
  reviewed: boolean;
  assignedTo: Nullable<string>;
  ddCall: Nullable<{ date: string; by: string }>;
  meetingNotes: string;
  impactNotes: string;
  /** The grid's Notes: one short internal line, empty for most. */
  notes: string;
  forbesId: string;
  allocation: Nullable<{ percent: number; notes: string; lastDone: string }>;
  suggestedLists: RankingFamily[];
  disclosures: IDemoDisclosure[];
  qualityHistory: IFieldChange[];
  travelHistory: IFieldChange[];
}

type PersonSeed = Omit<
  IDemoPerson,
  | 'disclosures'
  | 'qualityHistory'
  | 'travelHistory'
  | 'meetingNotes'
  | 'impactNotes'
  | 'notes'
  | 'submittedAt'
  | 'reviewed'
  | 'ddCall'
  | 'allocation'
  | 'suggestedLists'
> &
  Partial<IDemoPerson>;

function person(seed: PersonSeed): IDemoPerson {
  return {
    submittedAt: seed.status2027 === 'submitted' ? seed.lastAdvisorEdit : null,
    notes: '',
    disclosures: [],
    qualityHistory: [],
    travelHistory: [],
    meetingNotes: '',
    impactNotes: '',
    reviewed: false,
    ddCall: null,
    allocation: null,
    suggestedLists: [],
    ...seed
  };
}

export const DEMO_PEOPLE: readonly IDemoPerson[] = [
  person({
    id: 'p-whitfield',
    name: 'Dana Whitfield',
    idType: 'CRD',
    number: '9412077',
    email: 'dana.whitfield@example.com',
    firmId: 'f-merrill',
    city: 'Houston',
    state: 'TX',
    survey: 'FA',
    aum: 2350,
    production: 11.8,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-18',
    notes: 'Prefers a call after 3pm CT.',
    quality: { score: 9 },
    travel: '1',
    eligibility: 'Eligible',
    assignedTo: 's-priya',
    forbesId: 'FB-209331',
    suggestedLists: ['BIS', 'TW'],
    impactNotes: 'Founded a financial-literacy program for Houston public-school teachers in 2019.',
    meetingNotes: '2026 DD call: team of eight, two new hires in planning. Serves founders and physicians.',
    disclosures: [
      {
        id: 'd-whitfield-1',
        type: 'Customer dispute',
        eventDate: '2026-09-09',
        status: 'Pending',
        allegation:
          'Client alleges an annuity exchange in 2022 was unsuitable for her liquidity needs. Damages requested: $180,000.'
      }
    ],
    qualityHistory: [
      { date: '2026-03-12', value: '9', by: 'Priya Natarajan' },
      { date: '2025-03-04', value: '8', by: 'Leo Fairbanks' }
    ],
    travelHistory: [{ date: '2025-03-04', value: '1', by: 'Leo Fairbanks' }],
    allocation: { percent: 80, notes: 'Self-reported; team split confirmed on the 2026 call.', lastDone: '2026-03-12' }
  }),
  person({
    id: 'p-oyelaran',
    name: 'Marcus Oyelaran',
    idType: 'CRD',
    number: '9388140',
    email: 'marcus.oyelaran@example.com',
    firmId: 'f-ms',
    city: 'Dallas',
    state: 'TX',
    survey: 'FA',
    aum: 3100,
    production: 14.2,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-11',
    quality: { score: 10 },
    travel: '1',
    eligibility: 'Eligible',
    reviewed: true,
    assignedTo: 's-leo',
    forbesId: 'FB-118702',
    suggestedLists: ['BIS'],
    qualityHistory: [{ date: '2026-03-10', value: '10', by: 'Leo Fairbanks' }],
    travelHistory: [{ date: '2024-02-20', value: '1', by: 'Leo Fairbanks' }]
  }),
  person({
    id: 'p-hale',
    name: 'Priscilla Hale',
    idType: 'CRD',
    number: '9501266',
    email: 'priscilla.hale@example.com',
    firmId: 'f-ubs',
    city: 'Austin',
    state: 'TX',
    survey: 'FA',
    aum: 1480,
    production: 7.6,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-26',
    submittedAt: '2026-09-20',
    quality: { score: 7 },
    travel: '2',
    eligibility: 'Not yet reviewed',
    assignedTo: 's-priya',
    forbesId: 'FB-330915',
    suggestedLists: ['BIS', 'TW'],
    travelHistory: [{ date: '2026-03-15', value: '2', by: 'Priya Natarajan' }],
    qualityHistory: [{ date: '2026-03-15', value: '7', by: 'Priya Natarajan' }]
  }),
  person({
    id: 'p-brandt',
    name: 'Theo Brandt',
    idType: 'CRD',
    number: '9276013',
    email: 'theo.brandt@example.com',
    firmId: 'f-wells',
    city: 'Atlanta',
    state: 'GA',
    survey: 'FA',
    aum: 1920,
    production: 9.1,
    status2027: 'draft',
    lastAdvisorEdit: '2026-04-02',
    quality: { reason: 'Did not provide updates' },
    travel: 'NO',
    eligibility: 'Not eligible',
    assignedTo: 's-leo',
    forbesId: 'FB-095218',
  }),
  person({
    id: 'p-vasquez',
    name: 'Renata Vasquez',
    idType: 'CRD',
    number: '9633481',
    email: 'renata.vasquez@example.com',
    firmId: 'f-rj',
    city: 'Atlanta',
    state: 'GA',
    survey: 'FA',
    aum: 890,
    production: 5.4,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-22',
    quality: { score: 8 },
    travel: '1',
    eligibility: 'Eligible',
    assignedTo: 's-priya',
    forbesId: 'FB-441270',
    suggestedLists: ['NG', 'TW', 'BIS'],
    qualityHistory: [{ date: '2026-03-18', value: '8', by: 'Priya Natarajan' }]
  }),
  person({
    id: 'p-castellanos',
    name: 'Owen Castellanos',
    idType: 'CRD',
    number: '9345592',
    email: 'owen.castellanos@example.com',
    firmId: 'f-lpl',
    city: 'San Antonio',
    state: 'TX',
    survey: 'FA',
    aum: 1210,
    production: 6.3,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-25',
    quality: { reason: 'Unresponsive OR no DD' },
    travel: 'NO',
    eligibility: 'Not eligible',
    assignedTo: 's-leo',
    forbesId: 'FB-277004',
  }),
  person({
    id: 'p-morimoto',
    name: 'Hana Morimoto',
    idType: 'CRD',
    number: '9587720',
    email: 'hana.morimoto@example.com',
    firmId: 'f-merrill',
    city: 'Houston',
    state: 'TX',
    survey: 'FA',
    aum: 760,
    production: 4.1,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-19',
    quality: null,
    travel: null,
    eligibility: 'Not yet reviewed',
    assignedTo: 's-priya',
    forbesId: 'FB-512488',
    suggestedLists: ['NG']
  }),
  person({
    id: 'p-reyes',
    name: 'Calvin Reyes',
    idType: 'CRD',
    number: '9310845',
    email: 'calvin.reyes@example.com',
    firmId: 'f-wells',
    city: 'Atlanta',
    state: 'GA',
    survey: 'FA',
    aum: 1340,
    production: 6.9,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-15',
    quality: { reason: 'Firm said do not consider' },
    travel: 'NO',
    eligibility: 'Not eligible',
    assignedTo: 's-leo',
    forbesId: 'FB-160339',
  }),
  person({
    id: 'p-brooks',
    name: 'Nadia Brooks',
    idType: 'CRD',
    number: '9469903',
    email: 'nadia.brooks@example.com',
    firmId: 'f-lpl',
    city: 'Austin',
    state: 'TX',
    survey: 'FA',
    aum: 980,
    production: 5.0,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-21',
    notes: 'Confirm the LPL start date before ranking.',
    quality: { reason: 'Left Firm' },
    travel: 'NO',
    eligibility: 'Not eligible',
    assignedTo: 's-priya',
    forbesId: 'FB-388161',
  }),
  person({
    id: 'p-lindqvist',
    name: 'Isaac Lindqvist',
    idType: 'CRD',
    number: '9254418',
    email: 'isaac.lindqvist@example.com',
    firmId: 'f-ms',
    city: 'Dallas',
    state: 'TX',
    survey: 'FA',
    aum: 2040,
    production: 10.5,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-17',
    notes: 'Waiting on Morgan Stanley’s account of the inquiry.',
    quality: { reason: 'pending U4' },
    travel: 'NO',
    eligibility: 'Under review',
    assignedTo: 's-grace',
    forbesId: 'FB-071925',
    disclosures: [
      {
        id: 'd-lindqvist-1',
        type: 'Regulatory',
        eventDate: '2025-11-03',
        status: 'Pending',
        allegation:
          'State regulator inquiry into the supervision of a former junior advisor’s trading in two client accounts.'
      },
      {
        id: 'd-lindqvist-2',
        type: 'Customer dispute',
        eventDate: '2019-06-21',
        status: 'Settled',
        allegation: 'Client alleged excessive concentration in energy equities. Settled for $42,500.'
      }
    ]
  }),
  person({
    id: 'p-park',
    name: 'Mei-Lin Park',
    idType: 'NPN',
    number: '19843221',
    email: 'meilin.park@example.com',
    firmId: 'f-northstar',
    city: 'Plano',
    state: 'TX',
    survey: 'FSP',
    aum: 540,
    production: 3.2,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-23',
    quality: { score: 8 },
    travel: '1',
    eligibility: 'Eligible',
    assignedTo: 's-leo',
    forbesId: 'FB-603317',
    suggestedLists: ['FSP_250'],
    disclosures: [
      {
        id: 'd-park-1',
        type: 'Regulatory',
        eventDate: '2026-08-30',
        status: 'Closed — no action',
        allegation: 'Texas Department of Insurance review of a late license renewal. Closed without action.'
      }
    ]
  }),
  person({
    id: 'p-ashworth',
    name: 'Julian Ashworth',
    idType: 'SEC_NUMBER',
    number: '801-990417',
    email: 'julian.ashworth@example.com',
    firmId: 'f-harborline',
    city: 'Fort Worth',
    state: 'TX',
    survey: 'RIA_FIRM',
    aum: 4600,
    production: 0,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-27',
    quality: { score: 9 },
    travel: '1',
    eligibility: 'Eligible',
    assignedTo: 's-elena',
    forbesId: 'FB-900412',
    suggestedLists: ['RIA']
  }),
  // A team's survey record (ruling R10), as Conga's "Team Surveys" tab has them — named after
  // the team, anchored on its lead's CRD, with its own internal fields.
  person({
    id: 'p-team-bayou',
    name: 'The Bayou Partners Group',
    idType: 'CRD',
    number: '9412077',
    email: 'bayou.partners@example.com',
    firmId: 'f-merrill',
    city: 'Houston',
    state: 'TX',
    survey: 'TEAMS',
    aum: 3110,
    production: 15.8,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-21',
    quality: { score: 9 },
    travel: '1',
    eligibility: 'Eligible',
    assignedTo: null,
    forbesId: 'FB-710233',
    suggestedLists: ['TOP_TEAMS']
  }),
  person({
    id: 'p-team-peachtree',
    name: 'Peachtree Legacy Team',
    idType: 'CRD',
    number: '9276013',
    email: 'peachtree.legacy@example.com',
    firmId: 'f-wells',
    city: 'Atlanta',
    state: 'GA',
    survey: 'TEAMS',
    aum: 3260,
    production: 14.2,
    status2027: 'draft',
    lastAdvisorEdit: '2026-09-28',
    quality: null,
    travel: null,
    eligibility: 'Not yet reviewed',
    assignedTo: null,
    forbesId: 'FB-710518',
    suggestedLists: ['TOP_TEAMS']
  }),
  person({
    id: 'p-team-hillcountry',
    name: 'Hill Country Wealth Group',
    idType: 'CRD',
    number: '9345592',
    email: 'hillcountry.wealth@example.com',
    firmId: 'f-lpl',
    city: 'San Antonio',
    state: 'TX',
    survey: 'TEAMS',
    aum: 2190,
    production: 11.4,
    status2027: 'submitted',
    lastAdvisorEdit: '2026-09-25',
    quality: { score: 7 },
    travel: '2',
    eligibility: 'Under review',
    assignedTo: null,
    forbesId: 'FB-710904',
    suggestedLists: ['TOP_TEAMS']
  })
];

export function personName(id: string): string {
  return DEMO_PEOPLE.find((entry) => entry.id === id)?.name ?? id;
}

/* ------------------------------------------------------------------------------------------------
 * The scored result (RNK-17) — the external algorithm's output, as imported
 * ---------------------------------------------------------------------------------------------- */

export interface IRankingList {
  id: string;
  family: RankingFamily;
  territory: string;
  /** Person ids in place order — place 1 first. */
  order: string[];
}

export const SCORED_RESULT_FILE = { name: 'BIS_TW_2027_scored_v3.csv', importedAt: '2026-09-30 16:42', by: 'Elena Duarte' };

export const DEMO_RANKING_LISTS: readonly IRankingList[] = [
  { id: 'l-bis-tx', family: 'BIS', territory: 'Texas', order: ['p-oyelaran', 'p-whitfield', 'p-lindqvist', 'p-hale', 'p-morimoto'] },
  { id: 'l-bis-ga', family: 'BIS', territory: 'Georgia', order: ['p-vasquez', 'p-brandt'] },
  { id: 'l-tw', family: 'TW', territory: 'National', order: ['p-whitfield', 'p-hale', 'p-vasquez'] },
  { id: 'l-ng', family: 'NG', territory: 'Southeast', order: ['p-vasquez', 'p-morimoto'] },
  { id: 'l-fsp', family: 'FSP_250', territory: 'National', order: ['p-park'] },
  { id: 'l-ria', family: 'RIA', territory: 'National', order: ['p-ashworth'] }
];

/* ------------------------------------------------------------------------------------------------
 * Setup (step 23)
 * ---------------------------------------------------------------------------------------------- */

export interface IEmailTemplate {
  id: string;
  name: string;
  purpose: string;
  subject: string;
  body: string;
}

export const DEMO_TEMPLATES: readonly IEmailTemplate[] = [
  {
    id: 'tpl-dd',
    name: 'DD call invitation',
    purpose: 'WF-09 outreach, first of three emails',
    subject: 'Your {{ranking}} due-diligence call',
    body: 'Dear {{first_name}},\n\nThank you for completing your {{cycle}} survey. As part of our research we would like to schedule a short call to learn more about your practice.\n\nPlease choose a time that suits you: {{booking_link}}\n\nKind regards,\n{{analyst_name}}\nVeritant'
  },
  {
    id: 'tpl-metrics',
    name: 'Updated metrics request',
    purpose: 'WF-10 outreach to advisors at firms that do not supply data',
    subject: 'Please update your figures for {{ranking}}',
    body: 'Dear {{first_name}},\n\nOur records show your assets and production as of {{data_as_of}}. Please update them in your survey, or reply with the current figures.\n\nThank you,\n{{analyst_name}}'
  },
  {
    id: 'tpl-reminder',
    name: 'Survey reminder',
    purpose: 'Reminder config, sent before the survey closes',
    subject: 'Your {{survey}} closes on {{closes_on}}',
    body: 'Dear {{first_name}},\n\nYour {{survey}} for {{cycle}} is still open. Your answers are saved as you go; it closes on {{closes_on}}.\n\n{{portal_link}}'
  }
];
